import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const businesses = [
  "Sole Trader",
  "Limited Company",
  "Driver / Gig Worker",
  "Landlord",
  "Tradesperson",
  "Startup / SME",
  "Other",
];

export const CTASection = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so we can reply.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "website-cta" });
      setSubmitted(true);
      toast.success("Thanks! We'll be in touch within 1 working day.");
      setForm({ name: "", email: "", phone: "", business_type: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="cta-section"
      className="relative bg-[#D9EAC1] text-[#2A3430] overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#C57A5A]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-20 h-[28rem] w-[28rem] rounded-full bg-[#EAF4DD] blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: copy */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C57A5A] font-medium">
              Let’s talk numbers
            </p>
            <h2
              data-testid="cta-heading"
              className="font-serif-display mt-3 text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-[#2A3430]"
            >
              Ready to take control of your finances?
            </h2>
            <p className="mt-5 text-[#2A3430]/80 max-w-md text-base sm:text-lg">
              Send us a message or pick up the phone — first call’s on us, no
              obligation, no jargon.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:ahmed@xabaccountants.com"
                data-testid="cta-email-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#C57A5A] hover:bg-[#A96444] text-white font-medium px-6 py-3.5 rounded-full transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5"
              >
                <Mail className="h-4 w-4" /> Email us
              </a>
              <a
                href="tel:+442080640905"
                data-testid="cta-phone-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#2A3430] text-white hover:bg-[#1F2A22] font-medium px-6 py-3.5 rounded-full transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5"
              >
                <Phone className="h-4 w-4" /> Call us
              </a>
            </div>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md text-sm text-[#2A3430]/80">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#C57A5A]" /> Free 20-min discovery call
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#C57A5A]" /> Same-day reply guarantee
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#C57A5A]" /> No long contracts
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#C57A5A]" /> Qualified accountants
              </li>
            </ul>
          </div>

          {/* Right: lead form */}
          <div
            data-testid="quote-form-card"
            className="rounded-3xl bg-white text-[#2A3430] p-7 md:p-9 shadow-2xl shadow-[#2A3430]/10 ring-1 ring-[#2A3430]/8"
          >
            {submitted ? (
              <div data-testid="quote-form-success" className="text-center py-10">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#F5EEE9] text-[#C57A5A]">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <h3 className="font-serif-display text-2xl mt-5">Thanks — message received.</h3>
                <p className="mt-2 text-sm text-[#2A3430]/75 max-w-sm mx-auto">
                  A real person from Xab Accountants will be in touch within 1
                  working day. Check your inbox (and spam, just in case).
                </p>
                <button
                  data-testid="quote-form-reset"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm text-[#C57A5A] hover:text-[#A96444] underline underline-offset-4"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="quote-form">
                <div>
                  <h3 className="font-serif-display text-2xl">Get a free quote</h3>
                  <p className="text-sm text-[#2A3430]/70 mt-1">
                    Tell us a bit about you — we’ll come back with a fixed price.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Your name" required>
                    <input
                      data-testid="quote-input-name"
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      required
                      className={inputCls}
                      placeholder="Ahmed Ali"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      data-testid="quote-input-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      required
                      className={inputCls}
                      placeholder="you@email.com"
                    />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Phone (optional)">
                    <input
                      data-testid="quote-input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      className={inputCls}
                      placeholder="07…"
                    />
                  </Field>
                  <Field label="Business type">
                    <select
                      data-testid="quote-input-business-type"
                      value={form.business_type}
                      onChange={update("business_type")}
                      className={inputCls}
                    >
                      <option value="">Choose one…</option>
                      {businesses.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label="What do you need help with?">
                  <textarea
                    data-testid="quote-input-message"
                    rows={3}
                    value={form.message}
                    onChange={update("message")}
                    className={`${inputCls} resize-none`}
                    placeholder="A few words about your business and where you're stuck."
                  />
                </Field>

                <button
                  type="submit"
                  data-testid="quote-submit-btn"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#C57A5A] hover:bg-[#A96444] disabled:opacity-70 disabled:cursor-not-allowed text-white font-medium px-6 py-3.5 rounded-full transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send my enquiry <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                <p className="text-[11px] text-[#2A3430]/55 text-center">
                  We’ll only use your details to reply. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full rounded-xl border border-[#2A3430]/15 bg-[#EAF4DD]/40 focus:bg-white focus:border-[#C57A5A] focus:ring-2 focus:ring-[#C57A5A]/20 outline-none px-4 py-3 text-sm text-[#2A3430] placeholder:text-[#2A3430]/40 transition";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-[#2A3430] mb-1.5">
      {label}
      {required && <span className="text-[#C57A5A] ml-0.5">*</span>}
    </span>
    {children}
  </label>
);

export default CTASection;
